import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useProjectCellMetrics } from '_/hooks/useExtractionCellMetrics';
import { useProjectCellRoutes } from '_/hooks/useProjectCellRoutes';
import { ICellMetrics } from '_/types/extractionCells/extractionCellMetrics.types';
import { IExtractionCell } from '_/types/extractionCells/extractionCells.types';
import { IProjectCellRouteColumn } from '_/types/projects/projectCellRoutes.types';
import Alert from 'antd/lib/alert';
import Col from 'antd/lib/col';
import Progress from 'antd/lib/progress';
import Row from 'antd/lib/row';
import React, { ReactElement, useEffect, useMemo } from 'react';

import classes from './ProjectCellMetrics.module.css';

const INFLIGHT_RUNS_MAX = 3000;
const INFLIGHT_TASKS_MAX = 300_000;
const INTERACTION_QUEUE_MAX = 200_000;

function gaugeColor(percent: number): string {
  if (percent >= 90) return '#f5222d';
  if (percent >= 70) return '#faad14';
  return '#52c41a';
}

function MetricGauge({ title, value, max }: { title: string; value?: number; max: number }): ReactElement {
  const percent = value === undefined ? 0 : Math.min(100, (value / max) * 100);
  return (
    <div className={classes.gauge}>
      <Progress
        type="dashboard"
        percent={percent}
        status={percent >= 90 ? 'exception' : 'normal'}
        strokeColor={gaugeColor(percent)}
        format={() => value ?? '-'}
      />
      <div className={classes.gaugeTitle}>{title}</div>
    </div>
  );
}

function dedupeCellsByRoute(projectCellRoutes: IProjectCellRouteColumn[]): IExtractionCell[] {
  const cellsById = new Map<string, IExtractionCell>();
  projectCellRoutes.forEach((route) => {
    if (
      route.extractionCell &&
      route.extractionCell.active &&
      !route.extractionCell.slug.includes('balancer') &&
      !cellsById.has(route.extractionCell.id)
    ) {
      cellsById.set(route.extractionCell.id, route.extractionCell);
    }
  });
  return Array.from(cellsById.values());
}

function CellMetricsBlock({ cell, metrics }: { cell: IExtractionCell; metrics?: ICellMetrics }): ReactElement {
  return (
    <div className={classes.cellBlock}>
      <div className={classes.cellHeader}>
        {cell.active && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />}
        <h4 className={classes.cellSlug}>{cell.slug}</h4>
      </div>
      <Row className={classes.statisticRow} gutter={16}>
        <Col span={12}>
          <MetricGauge title="Inflight Runs" value={metrics?.inflightRuns} max={INFLIGHT_RUNS_MAX} />
        </Col>
        <Col span={12}>
          <MetricGauge title="Inflight Tasks" value={metrics?.inflightTasks} max={INFLIGHT_TASKS_MAX} />
        </Col>
      </Row>
      <Row className={classes.statisticRow} gutter={16}>
        <Col span={8}>
          <MetricGauge title="Interaction Queue (High)" value={metrics?.interactionQueues.high} max={INTERACTION_QUEUE_MAX} />
        </Col>
        <Col span={8}>
          <MetricGauge title="Interaction Queue (Med)" value={metrics?.interactionQueues.med} max={INTERACTION_QUEUE_MAX} />
        </Col>
        <Col span={8}>
          <MetricGauge title="Interaction Queue (Normal)" value={metrics?.interactionQueues.normal} max={INTERACTION_QUEUE_MAX} />
        </Col>
      </Row>
    </div>
  );
}

function ProjectCellMetrics(): ReactElement {
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();
  const { selectedOrgSlug: orgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const {
    data: projectCellRoutes = [],
    isLoading: isLoadingRoutes,
    isFetching: isFetchingRoutes,
    refetch: refetchRoutes,
  } = useProjectCellRoutes(selectedProject?.id);
  const {
    data: cellMetrics = [],
    isFetching: isFetchingMetrics,
    refetch: refetchMetrics,
  } = useProjectCellMetrics(selectedProject?.id);

  const cells = useMemo(() => dedupeCellsByRoute(projectCellRoutes), [projectCellRoutes]);
  const metricsByCellId = useMemo(() => {
    const map = new Map<string, ICellMetrics>();
    cellMetrics.forEach((metrics) => map.set(metrics.cellId, metrics));
    return map;
  }, [cellMetrics]);

  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${orgSlug}/projects/${selectedProjectSlug}/home`,
      },
      { title: 'Cell Metrics' },
    ]);

    setOnRefresh(() => {
      void refetchRoutes();
      void refetchMetrics();
    });

    return () => {
      setBreadcrumbs([]);
      setOnRefresh(undefined);
    };
  }, [
    setBreadcrumbs,
    setOnRefresh,
    selectedOrganization,
    selectedProject,
    selectedProjectSlug,
    orgSlug,
    refetchRoutes,
    refetchMetrics,
  ]);

  useEffect(() => {
    setRefreshLoading(isFetchingRoutes || isFetchingMetrics);
  }, [isFetchingRoutes, isFetchingMetrics, setRefreshLoading]);

  if (isLoadingRoutes) {
    return <Alert type="info" showIcon message="Loading cell metrics..." />;
  }

  if (cells.length === 0) {
    return <Alert type="info" showIcon message="No cell routes are configured for this project." />;
  }

  return (
    <>
      {cells.map((cell) => (
        <CellMetricsBlock key={cell.id} cell={cell} metrics={metricsByCellId.get(cell.id)} />
      ))}
    </>
  );
}

export default ProjectCellMetrics;
